import { pluralize, titleize } from 'inflection';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactElement, ReactNode } from 'react';
import { isValidElement } from 'react';
import type { SemanticICONS } from 'semantic-ui-react';
import { Button, Icon, Segment } from 'semantic-ui-react';

import { getDocsUrl } from '@routes/utils';

import s from './empty-placeholder.scss';

export interface EmptyPlaceholderProps {
  /**
   * When true, renders the primary action button in a disabled state. Use this
   * to signal to the user that they lack permission to create the item,
   * rather than hiding the button entirely.
   */
  disabled?: boolean;
  docsLink?: string;
  docsMessage?: ReactNode;
  filtering?: boolean;
  /**
   * Either a Semantic UI icon name (string) or a React element (e.g. a
   * Lucide icon component), rendered inside a circular badge.
   */
  icon?: ReactElement | SemanticICONS;
  /**
   * A free-standing illustration (e.g. an `img`) rendered in place of `icon`,
   * without the circular badge.
   */
  illustration?: ReactNode;
  itemName?: string;
  onCreateItem?: () => void;

  /**
   * Verb for the primary action button. Combined with the singular item name
   * to form the button label (e.g. "Add Asset"). Defaults to `"Create"`.
   */
  primaryAction?: string;
  /**
   * Overrides the generated heading with custom content.
   */
  title?: ReactNode;
}

export const EmptyPlaceholder = ({
  disabled,
  docsLink,
  docsMessage,
  filtering,
  icon,
  illustration,
  itemName = '',
  onCreateItem,
  primaryAction = 'Create',
  title,
}: EmptyPlaceholderProps) => {
  useStyles(s);

  const singularItemName = titleize(itemName);
  const pluralItemName = pluralize(singularItemName);

  return (
    <div className={s.root}>
      <Segment basic placeholder className={s.container}>
        {illustration ? (
          <span className={s.illustration}>{illustration}</span>
        ) : isValidElement(icon) ? (
          <span className={s.iconCircle}>{icon}</span>
        ) : icon ? (
          <Icon
            name={icon as SemanticICONS}
            size="huge"
            circular
            className={s.icon}
          />
        ) : null}
        <h2>
          {title ??
            (filtering
              ? `No ${pluralItemName} exist for the current filters`
              : `You don't have any ${pluralItemName} yet`)}
        </h2>
        <p className={s.docsMessage}>{docsMessage}</p>
        {docsLink || onCreateItem ? (
          <div className={s.buttons}>
            {docsLink ? (
              <Button
                className={s.button}
                icon
                size="large"
                basic
                color="blue"
                as="a"
                target="_blank"
                rel="noreferrer noopener"
                href={getDocsUrl(docsLink)}
              >
                Learn more&nbsp;
                <Icon name="external square alternate" size="large" />
              </Button>
            ) : null}
            {onCreateItem ? (
              <Button
                onClick={onCreateItem}
                primary
                size="large"
                disabled={disabled}
                className={s.button}
              >
                <Icon name="plus" size="large" /> {primaryAction}{' '}
                {singularItemName}
              </Button>
            ) : null}
          </div>
        ) : null}
      </Segment>
    </div>
  );
};

export default EmptyPlaceholder;
